<template>
  <div class="accreditation-section">
    <div class="content-wrapper">
      <div class="text-content">
        <h2 class="title">
          <span class="title-main">{{ t('accreditation.internationally') }}</span> <br>
          <span class="title-highlight">{{ t('accreditation.high_quality') }}</span> <span class="title-main">{{ t('accreditation.every_step') }}</span>
        </h2>
        <p class="description">
          {{ t('accreditation.description') }}
        </p>
      </div>

      <!-- Accreditation Badges - First Row -->
      <div class="badges-row">
        <img
          v-for="(image, index) in firstRowImages"
          :key="index"
          loading="lazy"
          :src="image"
          :alt="t('accreditation.badge_alt')"
          class="badge-img"
        />
      </div>

      <!-- Accreditation Badges - Second Row -->
      <div class="badges-row">
        <img
          v-for="(image, index) in secondRowImages"
          :key="index + 7"
          loading="lazy"
          :src="image"
          :alt="t('accreditation.badge_alt')"
          class="badge-img"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useLanguage } from '@/composables/useLanguage'

const { t } = useLanguage()

const accreditationImages = [
  'https://monglish.b-cdn.net/accreditations/13.webp',
  'https://monglish.b-cdn.net/accreditations/10.webp',
  'https://monglish.b-cdn.net/accreditations/2.webp',
  'https://monglish.b-cdn.net/accreditations/16.webp',
  'https://monglish.b-cdn.net/accreditations/9.webp',
  'https://monglish.b-cdn.net/accreditations/3.webp',
  'https://monglish.b-cdn.net/accreditations/8.webp',
  'https://monglish.b-cdn.net/accreditations/5.webp',
  'https://monglish.b-cdn.net/accreditations/7.webp',
  'https://monglish.b-cdn.net/accreditations/4.webp',
  'https://monglish.b-cdn.net/accreditations/14.webp',
  'https://monglish.b-cdn.net/accreditations/12.webp',
  'https://monglish.b-cdn.net/accreditations/1.webp',
  'https://monglish.b-cdn.net/accreditations/11.webp'
];

const firstRowImages = computed(() => accreditationImages.slice(0, 7));
const secondRowImages = computed(() => accreditationImages.slice(7));
</script>

<style scoped>
.accreditation-section {
  width: 100%;
  max-width: 100vw;
  position: relative;
  padding: clamp(40px, 8vw, 80px) clamp(15px, 3vw, 20px);
  text-align: center;
  font-family: Poppins, sans-serif;
  box-sizing: border-box;
}

.content-wrapper {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(35px, 6vw, 50px);
  box-sizing: border-box;
}

.text-content {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  padding: 0 clamp(10px, 2vw, 20px);
  box-sizing: border-box;
}

.title {
  font-size: clamp(24px, 4.5vw, 48px);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: clamp(20px, 4vw, 30px);
  text-transform: uppercase;
  width: 100%;
}

.title-main {
  color: #333333;
  font-weight: 700;
}

.title-highlight {
  color: #ff8c42;
  font-weight: 800;
}

.description {
  font-size: clamp(13px, 1.5vw, 16px);
  line-height: 1.7;
  color: #333333;
  max-width: 1050px;
  width: 100%;
  margin: 0 auto;
  font-weight: 400;
}

.badges-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: clamp(12px, 3vw, 40px);
  flex-wrap: wrap;
  padding: 0 clamp(10px, 2vw, 20px);
  width: 100%;
  box-sizing: border-box;
}

.badge-img {
  width: clamp(90px, 10vw, 150px);
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.badge-img:hover {
  transform: scale(1.1);
}

/* Tablet */
@media (max-width: 1024px) {
  .accreditation-section {
    padding: 60px 30px;
  }

  .content-wrapper {
    gap: 40px;
  }

  .title {
    margin-bottom: 25px;
  }

  .badges-row {
    gap: clamp(12px, 2.5vw, 30px);
  }

  .badge-img {
    width: clamp(75px, 9vw, 120px);
  }
}

/* Mobile */
@media (max-width: 768px) {
  .accreditation-section {
    padding: clamp(40px, 7vw, 50px) clamp(15px, 3vw, 20px);
  }

  .content-wrapper {
    gap: clamp(30px, 5vw, 35px);
  }

  .title {
    margin-bottom: clamp(16px, 3vw, 20px);
  }

  .description {
    padding: 0 clamp(8px, 1.5vw, 10px);
  }

  .badges-row {
    gap: clamp(10px, 2.5vw, 20px);
    padding: 0 clamp(8px, 1.5vw, 10px);
  }

  .badge-img {
    width: clamp(60px, 13vw, 90px);
  }
}

@media (max-width: 640px) {
  .accreditation-section {
    padding: clamp(35px, 6vw, 45px) clamp(12px, 2.5vw, 18px);
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .accreditation-section {
    padding: clamp(30px, 5.5vw, 40px) clamp(10px, 2vw, 15px);
  }

  .content-wrapper {
    gap: clamp(25px, 4.5vw, 30px);
  }

  .title {
    margin-bottom: clamp(12px, 2.5vw, 15px);
  }

  .description {
    line-height: 1.6;
    padding: 0 8px;
  }

  .badges-row {
    gap: clamp(10px, 2vw, 12px);
    padding: 0 8px;
  }

  .badge-img {
    width: clamp(55px, 12vw, 60px);
  }
}

@media (max-width: 380px) {
  .accreditation-section {
    padding: 25px 10px;
  }

  .content-wrapper {
    gap: 20px;
  }

  .title {
    margin-bottom: 10px;
  }

  .badges-row {
    gap: 8px;
    padding: 0 5px;
  }

  .badge-img {
    width: 50px;
  }
}
</style>
